import { Link } from 'react-router-dom';
import SEO, { articleJsonLd, breadcrumbJsonLd, faqJsonLd, SITE_URL } from '../../components/SEO';
import type { FAQItem } from '../../components/comparison/FAQSection';
import { ArticleSections } from '../blog/BlogSections';
import type { BlogPost } from '../blog/blogTypes';
import ComparisonHero, { type ComparisonValueHighlight } from '../../components/comparison/ComparisonHero';
import ComparisonTable from '../../components/comparison/ComparisonTable';
import FeatureSection from '../../components/comparison/FeatureSection';
import CTASection from '../../components/comparison/CTASection';
import FAQSection from '../../components/comparison/FAQSection';
import { getComparisonTheme } from '../../components/comparison/comparisonVisualThemes';
import ComparisonAICommandCentreSection from '../../components/comparison/ComparisonAICommandCentreSection';
import type { CompetitorAlternativeData } from './data';

const INTERNAL_SOLUTION_LINKS: { to: string; label: string }[] = [
  { to: '/compare', label: 'Compare all platforms vs Geofleet' },
  { to: '/features/ai-agents', label: 'AI Agents & automation' },
  { to: '/features/route-planner', label: 'Route optimization software' },
  { to: '/features/driver-app', label: 'Fleet management software' },
  { to: '/features/branded-tracking', label: 'Last-mile delivery software' },
];

const VALUE_HIGHLIGHTS: ComparisonValueHighlight[] = [
  { icon: 'ri-robot-2-line', title: 'Execution, not only planning', sub: 'One platform for allocation, routing, tracking, and exceptions—not siloed point tools.' },
  { icon: 'ri-truck-line', title: 'AI agents in the command centre', sub: 'Autonomous carrier and driver matching, SLA-aware alerts, and replanning as conditions change.' },
  { icon: 'ri-global-line', title: 'Built for real networks', sub: 'Hubs, carriers, shipper portals, and analytics aligned to how logistics actually runs.' },
];

const GEOFLEET_AI_COMMAND_STRENGTH =
  'AI Command Centre with AI Agents: autonomous carrier & driver allocation, agentic route planning, and intelligent alerts—not disconnected tools or manual playbooks.';

/** Guide FAQs first, then the page's own FAQs that don't repeat a guide question. */
function mergeFaqs(first: FAQItem[], second: FAQItem[]): FAQItem[] {
  const norm = (q: string) => q.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const seen = new Set(first.map((f) => norm(f.question)));
  return [...first, ...second.filter((f) => !seen.has(norm(f.question)))];
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** The merged long-form guide: key takeaways, intro and article body with the Geofleet box. */
function GuideArticle({ guide, pagePath }: { guide: BlogPost; pagePath: string }) {
  // The guide's "keep comparing" links may point back at this page now that it lives here.
  const sections = (guide.sections ?? []).map((section) =>
    section.type === 'relatedReading'
      ? { ...section, links: section.links.filter((l) => l.to !== pagePath) }
      : section
  );
  const updated = guide.updated ?? guide.date;
  return (
    <section className="pt-12 lg:pt-16 pb-16 lg:pb-20 bg-white" aria-label="Comparison guide">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-sm text-slate-500 mb-6">
          By {guide.author} · Updated {formatDate(updated)} · {guide.readTime}
        </p>
        {guide.intro && (
          <p className="text-lg text-slate-600 leading-relaxed italic border-l-4 border-slate-200 pl-4 mb-10">
            {guide.intro}
          </p>
        )}
        {guide.keyTakeaways && guide.keyTakeaways.length > 0 && (
          <div className="mb-12 rounded-2xl border border-blue-100 bg-indigo-50/90 px-6 py-6 lg:px-8 lg:py-7">
            <p className="flex items-center gap-2 text-blue-900 font-semibold mb-5">
              <i className="ri-key-line text-xl text-blue-700" aria-hidden />
              Key takeaways
            </p>
            <ul className="space-y-4 text-gray-700 leading-relaxed">
              {guide.keyTakeaways.map((item) => (
                <li key={item.lead}>
                  <span className="font-bold text-amber-900">{item.lead}</span> <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="prose prose-gray max-w-none">
          <ArticleSections sections={sections} proofPoint={guide.proofPoint} withPitch />
        </div>
      </div>
    </section>
  );
}

export default function CompetitorAlternativePage({ data }: { data: CompetitorAlternativeData }) {
  const path = `/${data.pathSlug}`;
  const pageUrl = `${SITE_URL}${path}`;
  const theme = getComparisonTheme(data.visualVariant);
  const guide = data.guide;
  const faqs = mergeFaqs(guide?.faq ?? [], data.faqs);

  return (
    <article className="pt-0 bg-white">
      <SEO
        title={data.documentTitle}
        exactDocumentTitle
        description={data.metaDescription}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: SITE_URL },
            { name: `${data.competitorName} alternative`, url: pageUrl },
          ]),
          faqJsonLd(faqs),
          ...(guide
            ? [
                articleJsonLd({
                  headline: data.h1,
                  description: data.metaDescription,
                  url: pageUrl,
                  datePublished: guide.date,
                  dateModified: guide.updated,
                  authorName: guide.author,
                  section: 'Comparisons',
                }),
              ]
            : []),
        ]}
      />
      <ComparisonHero
        competitorName={data.competitorName}
        h1={data.h1}
        subtext={data.heroSubtext}
        pageSlug={data.pathSlug}
        theme={theme}
        valueHighlights={VALUE_HIGHLIGHTS}
      />

      {guide ? (
        <>
          <GuideArticle guide={guide} pagePath={path} />
          <ComparisonAICommandCentreSection theme={theme} />
        </>
      ) : (
        <>
          <section className="relative pb-14 lg:pb-16 bg-white" aria-label="Introduction">
            <div className="max-w-3xl mx-auto px-6">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed text-center">
                {data.introParagraph}
              </p>
            </div>
          </section>

          <ComparisonAICommandCentreSection theme={theme} />

          <section
            className="pb-20 bg-slate-50/50 scroll-mt-24"
            aria-labelledby="vs-heading"
            id="competitor-comparison-table"
          >
            <div className="max-w-5xl mx-auto px-6 pt-4">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                <div className="text-center lg:text-left max-w-3xl lg:max-w-none mx-auto lg:mx-0">
                  <h2
                    id="vs-heading"
                    className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-3"
                  >
                    {data.competitorName} vs Geofleet at a glance
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    Side-by-side capabilities: where each platform focuses, and where Geofleet adds execution depth
                    for your network.
                  </p>
                </div>
                <span className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-500 shrink-0 mx-auto lg:mx-0">
                  <i className="ri-scales-3-line" style={{ color: theme.primary }} aria-hidden />
                  Feature comparison
                </span>
              </div>
              <ComparisonTable
                competitorLabel={data.competitorName}
                rows={data.tableRows}
                theme={theme}
                id={`comparison-table-${data.pathSlug}`}
              />
            </div>
          </section>

          <FeatureSection
            id="key-differences"
            title="Key Differences"
            variant="white"
            eyebrow="Honest comparison"
            theme={theme}
          >
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8 not-prose">
              <div className="rounded-3xl border border-slate-200/90 bg-slate-50/50 p-6 lg:p-8 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
                  <span
                    className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600"
                    aria-hidden
                  >
                    <i className="ri-thumb-up-line" />
                  </span>
                  Where {data.competitorName} is strong
                </h3>
                <ul className="space-y-3.5 text-slate-600 text-[15px] leading-relaxed">
                  {data.competitorStrengths.map((s) => (
                    <li key={s} className="flex gap-3">
                      <span className="text-slate-400 flex-shrink-0 mt-1.5">▸</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className="rounded-3xl border p-6 lg:p-8 shadow-md not-prose"
                style={{
                  borderColor: `rgba(${theme.primaryRgb}, 0.35)`,
                  background: `linear-gradient(145deg, rgba(${theme.primaryRgb}, 0.07) 0%, #fff 55%)`,
                  boxShadow: `0 20px 50px -28px rgba(${theme.primaryRgb}, 0.25)`,
                }}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: theme.primary }}
                    aria-hidden
                  >
                    <i className="ri-rocket-2-line" />
                  </span>
                  Where Geofleet is stronger
                </h3>
                <ul className="space-y-3.5 text-slate-700 text-[15px] leading-relaxed">
                  <li className="flex gap-3">
                    <i
                      className="ri-checkbox-circle-fill flex-shrink-0 mt-0.5 text-lg"
                      style={{ color: theme.primary }}
                      aria-hidden
                    />
                    <span>{GEOFLEET_AI_COMMAND_STRENGTH}</span>
                  </li>
                  {data.geofleetStrengths.map((s) => (
                    <li key={s} className="flex gap-3">
                      <i
                        className="ri-checkbox-circle-fill flex-shrink-0 mt-0.5 text-lg"
                        style={{ color: theme.primary }}
                        aria-hidden
                      />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FeatureSection>

          <FeatureSection
            id="why-geofleet"
            title="Why Choose Geofleet"
            variant="muted"
            eyebrow="Execution at scale"
            theme={theme}
          >
            <ul className="space-y-4 list-none not-prose pl-0 max-w-3xl">
              <li
                className="flex gap-4 text-slate-600 text-[15px] leading-relaxed items-start bg-white rounded-2xl border border-slate-100 px-5 py-4 shadow-sm"
              >
                <span
                  className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white text-lg"
                  style={{ backgroundColor: theme.primary }}
                  aria-hidden
                >
                  <i className="ri-cpu-line" />
                </span>
                <span className="pt-1">
                  Run logistics automatically from the Geofleet command centre: AI Agents handle exceptions,
                  auto carrier allocation and auto driver allocation stay in sync, routes replan themselves, and
                  alerts fire before SLAs break—without the patchwork you see elsewhere.
                </span>
              </li>
              {data.whyGeofleetBullets.map((b) => (
                <li
                  key={b}
                  className="flex gap-4 text-slate-600 text-[15px] leading-relaxed items-start bg-white rounded-2xl border border-slate-100 px-5 py-4 shadow-sm"
                >
                  <span
                    className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white text-lg"
                    style={{ backgroundColor: theme.primary }}
                    aria-hidden
                  >
                    <i className="ri-check-line" />
                  </span>
                  <span className="pt-1">{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-10 text-slate-600 not-prose text-[15px] leading-relaxed max-w-3xl">
              Explore how Geofleet supports{' '}
              <Link
                to="/features/route-planner"
                className="font-semibold hover:underline"
                style={{ color: theme.primary }}
              >
                route optimization software
              </Link>
              ,{' '}
              <Link
                to="/features/driver-app"
                className="font-semibold hover:underline"
                style={{ color: theme.primary }}
              >
                fleet operations
              </Link>
              , and{' '}
              <Link
                to="/features/branded-tracking"
                className="font-semibold hover:underline"
                style={{ color: theme.primary }}
              >
                last-mile delivery software
              </Link>{' '}
              in one stack.
            </p>
          </FeatureSection>

          <FeatureSection
            id="when-to-use"
            title={`When to Use ${data.competitorName}`}
            variant="white"
            theme={theme}
          >
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8 not-prose">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 lg:p-8 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-5">
                  Use {data.competitorName} if:
                </h3>
                <ul className="space-y-3.5 text-slate-600 text-[15px] leading-relaxed">
                  {data.useCompetitorIf.map((s) => (
                    <li key={s} className="flex gap-3">
                      <span className="text-slate-300 flex-shrink-0">—</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className="rounded-3xl border-2 p-6 lg:p-8 shadow-md"
                style={{
                  borderColor: `rgba(${theme.primaryRgb}, 0.45)`,
                  background: `linear-gradient(160deg, rgba(${theme.primaryRgb}, 0.08) 0%, #fff 50%)`,
                }}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-5">Use Geofleet if:</h3>
                <ul className="space-y-3.5 text-slate-700 text-[15px] leading-relaxed">
                  {data.useGeofleetIf.map((s) => (
                    <li key={s} className="flex gap-3">
                      <i
                        className="ri-arrow-right-circle-fill flex-shrink-0 mt-0.5 text-xl"
                        style={{ color: theme.primary }}
                        aria-hidden
                      />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FeatureSection>
        </>
      )}

      {data.relatedGuides && data.relatedGuides.length > 0 && (
        <section className="py-14 bg-white" aria-labelledby="guides-heading">
          <div className="max-w-5xl mx-auto px-6">
            <h2 id="guides-heading" className="text-lg font-semibold text-slate-900 mb-5">
              In-depth {data.competitorName} guides
            </h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {data.relatedGuides.map((g) => (
                <li key={g.to}>
                  <Link
                    to={g.to}
                    className="flex items-center justify-between gap-3 h-full px-5 py-4 rounded-2xl text-sm font-medium bg-slate-50 text-slate-800 border border-slate-200 hover:border-slate-300 transition-all"
                  >
                    {g.label}
                    <i className="ri-arrow-right-line text-base opacity-60 shrink-0" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="py-14 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-lg font-semibold text-slate-900 mb-5">Related capabilities</p>
          <nav aria-label="Related solution pages">
            <ul className="flex flex-wrap gap-3">
              {INTERNAL_SOLUTION_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-medium bg-white text-slate-800 border border-slate-200 hover:border-slate-300 transition-all shadow-sm hover:shadow"
                  >
                    {l.label}
                    <i className="ri-arrow-right-up-line text-base opacity-60" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      <CTASection
        headline="Ready to optimize your logistics operations?"
        subtext="See how Geofleet unifies route optimization, allocation, tracking, and analytics in one logistics automation platform."
        pageSlug={data.pathSlug}
        competitorName={data.competitorName}
        theme={theme}
      />

      <FAQSection title="FAQs" items={faqs} theme={theme} />
    </article>
  );
}
